import {
  Box,
  Button,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BaseModal from "../BaseModal/BaseModal";
import * as Styled from "./CreatedPollModal.styles";
import { CoffeeMug, Poll } from "../../assets";
import {
  AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PollResultsModal from "../PollResultsModal/PollResultsModal";
import {
  POLL_STATUS,
  Poll as PollObject,
  transformResponseToPoll,
} from "./PollDataModel";
import {
  deleteAllPollsForEvent,
  deletePollForEvent,
  getAllPollsForEvent,
  updatePollStatus,
} from "../../services/appsync";

type CreatedPollModalProps = {
  open: boolean;
  onClose: () => void;
  eventId: string;
  onPushPoll?: (pollId: string) => void;
  pushedPollId?: string | null;
  variant?: "upcoming-event" | "past-event" | "live-event";
};

const CreatedPollModal = ({
  open,
  onClose,
  eventId,
  variant,
  onPushPoll,
  pushedPollId,
}: CreatedPollModalProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const [showPollResultsModal, setShowPollResultsModal] = useState(false);
  const [selectedPoll, setSelectedPoll] = useState<string | null>(null);
  const [pollActionType, setPollActionType] = useState<"push" | "close" | null>(
    null
  );
  const [showPushError, setShowPushError] = useState(false);
  const [isLoadingPolls, setIsLoadingPolls] = useState(false);

  const [polls, setPolls] = useState<PollObject[]>([]);

  const isPollPushed = (pollId: string) =>
    polls.find(
      (poll) => poll.status === (POLL_STATUS.PUSHED || POLL_STATUS.COMPLETED)
    )?.pollId === pollId;

  const handleNewPollCreation = () => {
    navigate(`/createpoll`, { state: { passedEventId: eventId } });
  };

  const handleEditPollSelect = () => {
    if (selectedPoll !== null) {
      navigate("/editpoll", {
        state: { pollObj: polls.find((poll) => poll.pollId === selectedPoll) },
      });
    } else {
      console.log("No poll selected for editing");
    }
  };

  const handlePollSelect = (pollId: string) => {
    setShowPushError(false);

    if (selectedPoll === pollId) {
      setSelectedPoll(null);
    } else {
      setSelectedPoll(pollId);
    }

    if (
      polls.find(
        (poll) =>
          poll.pollId === pollId && poll.status === POLL_STATUS.COMPLETED
      )?.pollId === pollId
    ) {
      setTimeout(() => {
        setShowPollResultsModal(true);
      }, 100);
    }
  };

  const getAllPollsForThisEvent = useCallback(
    async (eventId: string = "") => {
      setIsLoadingPolls(true);
      try {
        let responsePayload;
        if (variant === "past-event") {
          responsePayload = await getAllPollsForEvent(
            eventId,
            undefined,
            undefined,
            POLL_STATUS.COMPLETED
          );
        } else {
          responsePayload = await getAllPollsForEvent(eventId);
        }
        setPolls(transformResponseToPoll(responsePayload));
      } catch (error) {
        console.error("Error getting polls: ", error);
      } finally {
        setIsLoadingPolls(false);
      }
    },
    [variant]
  );

  useEffect(() => {
    if (open) {
      setSelectedPoll(null);
      getAllPollsForThisEvent(eventId);
    }
  }, [open, eventId, getAllPollsForThisEvent]);

  useEffect(() => {
    if (!selectedPoll) {
      setShowPushError(false);
    }
  }, [selectedPoll]);

  const handleDeletePoll = async () => {
    if (selectedPoll !== null) {
      try {
        await deletePollForEvent(eventId, selectedPoll);

        setPolls((prevPolls) =>
          prevPolls.filter((poll) => poll.pollId !== selectedPoll)
        );
        setSelectedPoll(null);
      } catch (error) {
        console.error("Error deleting poll: ", error);
      }
    }
  };

  const handlePollResultsModalClose = () => {
    setShowPollResultsModal(false);

    setSelectedPoll(null);

    // Refresh polls data when returning from PollResultsModal
    getAllPollsForThisEvent(eventId);
  };

  const handleDeleteAllPolls = async () => {
    setSelectedPoll(null);

    await deleteAllPollsForEvent(
      eventId,
      polls.filter((poll) => !isPollPushed(poll.pollId))
    );

    onClose();

    setTimeout(() => {
      setPolls([]);
    }, 300);
  };

  const handlePushPoll = async () => {
    // Check if there is already a pushed poll that is not completed
    const pushedPoll = polls.find((poll) => poll.status === POLL_STATUS.PUSHED);
    if (pushedPoll && selectedPoll !== pushedPoll.pollId) {
      setShowPushError(true);
      return;
    }
    setShowPushError(false);

    if (selectedPoll !== null && onPushPoll) {
      setPollActionType("push");
      setShowPollResultsModal(true);
      onPushPoll(selectedPoll);
      onClose();
    } else {
      // From Manage Events Page
      // Handle push within dialog
      if (selectedPoll !== null && eventId) {
        try {
          await updatePollStatus(eventId, selectedPoll, POLL_STATUS.PUSHED);
          onClose();
        } catch (error) {
          console.error("Error pushing poll: ", error);
        }
      }
    }
  };

  const handleClosePoll = async () => {
    // From Manage Events Page
    // Handle close within dialog
    if (selectedPoll !== null && eventId) {
      try {
        await updatePollStatus(eventId, selectedPoll, POLL_STATUS.COMPLETED);
        if (variant === "live-event") {
          setPollActionType("close");
          setShowPollResultsModal(true);
        } else {
          onClose();
        }
      } catch (error) {
        console.error("Error closing poll: ", error);
      }
    }
  };

  const handleCloseModal = () => {
    setSelectedPoll(null);
    onClose();
    setShowPushError(false);
  };

  return (
    <>
      {open && (
        <BaseModal
          open={open}
          onClose={handleCloseModal}
          desktopWidth="880px"
          padding="24px 0px 0px 0px"
          overflow="auto"
        >
          <>
            {isLoadingPolls ? (
              <Box
                padding="40px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="h6">Loading polls...</Typography>
              </Box>
            ) : polls.length === 0 ? (
              <>
                {/* no polls created */}
                <Box padding="40px">
                  <Styled.PollTitle>Event polls</Styled.PollTitle>
                  <Box
                    sx={{
                      justifyContent: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={CoffeeMug}
                      alt="Polls"
                      style={{
                        marginBottom: "24px",
                        height: "125px",
                        width: "125px",
                      }}
                    />
                    <Styled.PollSubtitle>
                      Nothing to see here...
                    </Styled.PollSubtitle>
                    <Styled.PollSubtitle2>
                      A poll hasn’t been created for this event.
                    </Styled.PollSubtitle2>
                    <Button
                      variant="contained"
                      onClick={handleNewPollCreation}
                      startIcon={<AddCircleOutlineOutlinedIcon />}
                    >
                      Create new poll
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {/* polls list  */}
                <Box padding="0px 40px">
                  <Styled.ExistingPollsTitle>
                    Event polls ({polls.length})
                  </Styled.ExistingPollsTitle>
                  {variant === "past-event" ? (
                    <Styled.ExisitingPollsSubtitle2>
                      Select a poll you wish to view the results for.
                    </Styled.ExisitingPollsSubtitle2>
                  ) : (
                    <Styled.ExisitingPollsSubtitle2>
                      {variant === "upcoming-event"
                        ? "Select a poll you wish to manage."
                        : "Select a poll you wish to push to the event."}
                    </Styled.ExisitingPollsSubtitle2>
                  )}

                  {/* Created Polls */}

                  <Box>
                    {polls
                      .filter(
                        (poll) =>
                          !isPollPushed(poll.pollId) &&
                          poll.status === POLL_STATUS.CREATED &&
                          variant !== "past-event"
                      )
                      .map((poll) => {
                        return (
                          <Styled.SinglePollContainer
                            key={poll.pollId}
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            padding="20px 16px"
                            onClick={() => handlePollSelect(poll.pollId)}
                            sx={{
                              boxShadow:
                                selectedPoll === poll.pollId
                                  ? "0px 6px 20px 0px #0C62FB14"
                                  : "none",
                              cursor: "pointer",
                            }}
                          >
                            <img
                              src={Poll}
                              alt="Poll icon"
                              style={{ marginRight: "24px" }}
                            />
                            <Box display="flex" flexDirection="column">
                              <Styled.PollCreatedDate>
                                {poll.dateCreated}
                              </Styled.PollCreatedDate>
                              <Styled.PollQuestion>
                                {poll.question}
                              </Styled.PollQuestion>
                            </Box>
                          </Styled.SinglePollContainer>
                        );
                      })}
                  </Box>

                  {/* Pushed Poll */}

                  {(variant === "live-event" || variant === "upcoming-event") &&
                    polls.some(
                      (poll) =>
                        isPollPushed(poll.pollId) &&
                        poll.status === POLL_STATUS.PUSHED
                    ) && (
                      <>
                        <Divider sx={{ marginBottom: "24px" }} />

                        <Styled.PollPushedTitle>
                          Currently Pushed
                        </Styled.PollPushedTitle>

                        {polls
                          .filter(
                            (poll) =>
                              isPollPushed(poll.pollId) &&
                              poll.status === POLL_STATUS.PUSHED
                          )
                          .map((poll) => (
                            <Styled.SinglePollContainer
                              key={poll.pollId}
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              padding="20px 16px"
                              onClick={() => handlePollSelect(poll.pollId)}
                              sx={{
                                boxShadow:
                                  selectedPoll === poll.pollId
                                    ? "0px 6px 20px 0px #0C62FB14"
                                    : "none",
                                cursor: "pointer",
                              }}
                            >
                              <img
                                src={Poll}
                                alt="Poll icon"
                                style={{ marginRight: "24px" }}
                              />
                              <Box display="flex" flexDirection="column">
                                <Styled.PollCreatedDate>
                                  {poll.dateTimePushed ? (
                                    <>
                                      Pushed on {poll.dateTimePushed.month}{" "}
                                      {poll.dateTimePushed.day}
                                      <sup>
                                        {poll.dateTimePushed.ordinal}
                                      </sup>{" "}
                                      at {poll.dateTimePushed.time}
                                    </>
                                  ) : (
                                    `Pushed on ${
                                      poll.dateTimePushed ?? "unknown"
                                    }`
                                  )}
                                </Styled.PollCreatedDate>
                                <Styled.PollQuestion>
                                  {poll.question}
                                </Styled.PollQuestion>
                              </Box>
                            </Styled.SinglePollContainer>
                          ))}
                      </>
                    )}

                  {/* Completed Polls */}

                  {(variant === "live-event" || variant === "upcoming-event") &&
                    polls.some(
                      (poll) => poll.status === POLL_STATUS.COMPLETED
                    ) && (
                      <>
                        <Divider sx={{ marginBottom: "24px" }} />

                        <Styled.PollPushedTitle>
                          Already Completed
                        </Styled.PollPushedTitle>

                        {polls
                          .filter(
                            (poll) => poll.status === POLL_STATUS.COMPLETED
                          )
                          .map((poll) => (
                            <Styled.SinglePollContainer
                              key={poll.pollId}
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              padding="16px 24px"
                              onClick={() => handlePollSelect(poll.pollId)}
                              sx={{
                                backgroundColor: "#F5F5F5",
                                opacity: 0.8,
                                borderRadius: "8px",
                                marginBottom: "40px",
                              }}
                            >
                              <img
                                src={Poll}
                                alt="Poll icon"
                                style={{ marginRight: "24px" }}
                              />
                              <Box display="flex" flexDirection="column">
                                <Styled.PollCreatedDate>
                                  {poll.dateTimePushed ? (
                                    <>
                                      Pushed on {poll.dateTimePushed.month}{" "}
                                      {poll.dateTimePushed.day}
                                      <sup>
                                        {poll.dateTimePushed.ordinal}
                                      </sup>{" "}
                                      at {poll.dateTimePushed.time}
                                    </>
                                  ) : (
                                    `Pushed on ${
                                      poll.dateTimePushed ?? "unknown"
                                    }`
                                  )}
                                </Styled.PollCreatedDate>
                                <Styled.PollQuestion>
                                  {poll.question}
                                </Styled.PollQuestion>
                              </Box>
                            </Styled.SinglePollContainer>
                          ))}
                      </>
                    )}

                  {/* Completed Polls - Past Event */}

                  {variant === "past-event" &&
                    polls.some(
                      (poll) => poll.status === POLL_STATUS.COMPLETED
                    ) && (
                      <>
                        {polls
                          .filter(
                            (poll) => poll.status === POLL_STATUS.COMPLETED
                          )
                          .map((poll) => (
                            <Styled.SinglePollContainer
                              key={poll.pollId}
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              padding="20px 16px"
                              onClick={() => handlePollSelect(poll.pollId)}
                              sx={{
                                boxShadow:
                                  selectedPoll === poll.pollId
                                    ? "0px 6px 20px 0px #0C62FB14"
                                    : "none",
                                cursor: "pointer",
                              }}
                            >
                              <img
                                src={Poll}
                                alt="Poll icon"
                                style={{ marginRight: "24px" }}
                              />
                              <Box display="flex" flexDirection="column">
                                <Styled.PollCreatedDate>
                                  {poll.dateTimePushed ? (
                                    <>
                                      Pushed on {poll.dateTimePushed.month}{" "}
                                      {poll.dateTimePushed.day}
                                      <sup>
                                        {poll.dateTimePushed.ordinal}
                                      </sup>{" "}
                                      at {poll.dateTimePushed.time}
                                    </>
                                  ) : (
                                    `Pushed on ${
                                      poll.dateTimePushed ?? "unknown"
                                    }`
                                  )}
                                </Styled.PollCreatedDate>
                                <Styled.PollQuestion>
                                  {poll.question}
                                </Styled.PollQuestion>
                              </Box>
                            </Styled.SinglePollContainer>
                          ))}
                      </>
                    )}
                </Box>
                {variant === "upcoming-event" && (
                  <Styled.ButtonContainer>
                    {" "}
                    <Button
                      onClick={
                        selectedPoll ? handleDeletePoll : handleDeleteAllPolls
                      }
                      startIcon={<DeleteOutlineIcon />}
                      sx={{ textDecoration: "underline" }}
                    >
                      {selectedPoll ? "Delete" : "Delete all polls"}
                    </Button>{" "}
                    {!selectedPoll ? (
                      <Button
                        variant="contained"
                        onClick={handleNewPollCreation}
                        startIcon={<AddCircleOutlineOutlinedIcon />}
                      >
                        Create new poll
                      </Button>
                    ) : polls.find((poll) => poll.pollId === selectedPoll)
                        ?.status === POLL_STATUS.PUSHED &&
                      variant === "upcoming-event" ? (
                      <Box
                        display="flex"
                        gap={2}
                        style={{ flexDirection: isMobile ? "column" : "row" }}
                      >
                        <Button variant="contained" onClick={handleClosePoll}>
                          Close Poll
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        display="flex"
                        gap={2}
                        style={{ flexDirection: isMobile ? "column" : "row" }}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleEditPollSelect}
                        >
                          Edit
                        </Button>
                        <Button variant="contained" onClick={handlePushPoll}>
                          Push
                        </Button>
                      </Box>
                    )}
                  </Styled.ButtonContainer>
                )}
                {variant === "live-event" && (
                  <Styled.ButtonContainerLive>
                    <Box>
                      {showPushError && (
                        <Styled.PushErrorMessage>
                          Please close the active poll before pushing another
                          poll to the event.
                        </Styled.PushErrorMessage>
                      )}
                    </Box>
                    <Box>
                      {!selectedPoll ? (
                        <Button
                          variant="contained"
                          onClick={handleNewPollCreation}
                          startIcon={<AddCircleOutlineOutlinedIcon />}
                        >
                          Create new poll
                        </Button>
                      ) : polls.find((poll) => poll.pollId === selectedPoll)
                          ?.status === POLL_STATUS.PUSHED ? (
                        <Button variant="contained" onClick={handleClosePoll}>
                          Close Poll
                        </Button>
                      ) : polls.find((poll) => poll.pollId === selectedPoll)
                          ?.status === POLL_STATUS.CREATED ? (
                        <Button variant="contained" onClick={handlePushPoll}>
                          Push
                        </Button>
                      ) : undefined}
                    </Box>
                  </Styled.ButtonContainerLive>
                )}
              </>
            )}
          </>
        </BaseModal>
      )}
      {showPollResultsModal && (
        <PollResultsModal
          open={showPollResultsModal}
          passedPoll={(() => {
            const foundPoll = polls.find(
              (poll) => poll.pollId === selectedPoll
            );

            return foundPoll
              ? { pollId: foundPoll.pollId, eventId: foundPoll.eventId }
              : undefined;
          })()}
          onClose={handlePollResultsModalClose}
          pollAlreadyClosed={pollActionType === "close"}
          variant={(() => {
            const selectedPollObj = polls.find(
              (poll) => poll.pollId === selectedPoll
            );
            if (
              (variant === "live-event" || variant === "upcoming-event") &&
              selectedPollObj?.status === POLL_STATUS.COMPLETED
            ) {
              return "past-event";
            }
            return variant;
          })()}
        />
      )}
    </>
  );
};

export default CreatedPollModal;
